import React, { useState, useEffect, useCallback } from "react";
import Tilt3D from "./Tilt3D";
import { BLUE, BLUE_DEEP, BLUE_LIGHT, BORDER, TEXT, TEXT2, TEXT3, WHITE } from "../../shared";

/**
 * AdPreviewCard
 * -------------
 * Renders a native-looking mockup of a generated ad. Tilts on hover, clicks
 * to expand with editing actions and estimated reach.
 *
 * Props:
 *   - adType: "GoogleSearch" | "GoogleDisplay" | "MetaFeed" | "InstagramStory" | "TikTokInFeed"
 *   - brand:        { name, displayUrl?, avatarUrl? }
 *   - headline:     string
 *   - description:  string
 *   - cta:          string                  (default "Learn More")
 *   - asset:        { url, type? }          (used by Display/Meta/Story/TikTok)
 *   - sitelinks:    Array<{ title, url? }>  (Google Search)
 *   - targeting:    string | string[]       (free-form audience description)
 *   - estimatedReach: number | string
 *   - displaySize:  "300x250" | "728x90"    (Google Display only — defaults to "300x250")
 *   - onEdit, onRegenerate, onLaunch
 */
export default function AdPreviewCard({
  adType = "MetaFeed",
  brand = {},
  headline = "",
  description = "",
  cta = "Learn More",
  asset = {},
  sitelinks,
  targeting,
  estimatedReach,
  displaySize = "300x250",
  onEdit,
  onRegenerate,
  onLaunch,
}) {
  const [expanded, setExpanded] = useState(false);
  const meta = AD_TYPE_META[adType] || AD_TYPE_META.MetaFeed;

  const close = useCallback(() => setExpanded(false), []);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, close]);

  const reachLabel = formatReach(estimatedReach);

  return (
    <>
      <Tilt3D
        onClick={() => setExpanded(true)}
        role="button"
        ariaLabel={`Open ${meta.label} ad preview`}
        style={{
          width: "100%",
          maxWidth: 380,
          background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(244,250,255,0.86) 100%)",
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 22px 50px -22px rgba(11,107,211,0.30), 0 10px 28px -14px rgba(14,165,233,0.18)",
          overflow: "hidden",
          padding: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "5px 12px 5px 6px", borderRadius: 999,
            background: BLUE_LIGHT, border: `1px solid ${BORDER}`,
          }}>
            <span aria-hidden style={{
              width: 22, height: 22, borderRadius: 6,
              background: meta.gradient,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 10, fontWeight: 800,
            }}>{meta.glyph}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{meta.label}</span>
          </div>
          {reachLabel ? (
            <div style={{
              fontSize: 10, fontWeight: 700, color: BLUE_DEEP,
              padding: "4px 10px", borderRadius: 999,
              background: BLUE_LIGHT, border: `1px solid ${BORDER}`,
              whiteSpace: "nowrap",
            }}>~{reachLabel} reach</div>
          ) : null}
        </div>

        <div style={{ borderRadius: 14, overflow: "hidden", boxShadow: "0 6px 18px -8px rgba(10,27,58,0.25)" }}>
          <AdMockup
            adType={adType}
            brand={brand}
            headline={headline}
            description={description}
            cta={cta}
            asset={asset}
            sitelinks={sitelinks}
            displaySize={displaySize}
            compact
          />
        </div>
      </Tilt3D>

      {expanded ? (
        <ExpandedAd
          adType={adType}
          brand={brand}
          headline={headline}
          description={description}
          cta={cta}
          asset={asset}
          sitelinks={sitelinks}
          displaySize={displaySize}
          targeting={targeting}
          estimatedReach={reachLabel}
          onClose={close}
          onEdit={onEdit}
          onRegenerate={onRegenerate}
          onLaunch={onLaunch}
        />
      ) : null}
    </>
  );
}

const AD_TYPE_META = {
  GoogleSearch:   { label: "Google Search",       glyph: "G",  gradient: "linear-gradient(135deg,#4285F4,#34A853 60%,#FBBC05)" },
  GoogleDisplay:  { label: "Google Display",      glyph: "G",  gradient: "linear-gradient(135deg,#34A853,#4285F4)" },
  MetaFeed:       { label: "Meta Feed",           glyph: "M",  gradient: "linear-gradient(135deg,#1877F2,#0a5dc2)" },
  InstagramStory: { label: "Instagram Story",     glyph: "Ig", gradient: "linear-gradient(135deg,#f09433,#dc2743,#bc1888)" },
  TikTokInFeed:   { label: "TikTok In-Feed",      glyph: "TT", gradient: "linear-gradient(135deg,#25F4EE,#000 50%,#FE2C55)" },
};

function formatReach(r) {
  if (r === null || r === undefined || r === "") return null;
  if (typeof r === "string") return r;
  if (typeof r !== "number" || !Number.isFinite(r)) return null;
  if (r >= 1_000_000) return (r / 1_000_000).toFixed(r >= 10_000_000 ? 0 : 1) + "M";
  if (r >= 1_000) return (r / 1_000).toFixed(r >= 10_000 ? 0 : 1) + "k";
  return String(Math.round(r));
}

function ExpandedAd({ adType, brand, headline, description, cta, asset, sitelinks, displaySize, targeting, estimatedReach, onClose, onEdit, onRegenerate, onLaunch }) {
  const targetingList = Array.isArray(targeting) ? targeting : (targeting ? [targeting] : []);
  const meta = AD_TYPE_META[adType] || AD_TYPE_META.MetaFeed;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${meta.label} ad preview`}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(10,27,58,0.55)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(980px, 100%)",
          maxHeight: "calc(100vh - 48px)",
          background: "rgba(255,255,255,0.96)",
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow: "0 30px 80px rgba(10,27,58,0.45)",
          padding: 28,
          display: "grid",
          gridTemplateColumns: "minmax(0, 420px) 1fr",
          gap: 28,
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEXT3, letterSpacing: 0.6, textTransform: "uppercase" }}>{meta.label}</div>
          <div style={{ borderRadius: 18, overflow: "hidden", boxShadow: "0 18px 40px -16px rgba(10,27,58,0.35)" }}>
            <AdMockup
              adType={adType}
              brand={brand}
              headline={headline}
              description={description}
              cta={cta}
              asset={asset}
              sitelinks={sitelinks}
              displaySize={displaySize}
            />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h2 style={{ margin: 0, fontSize: 20, color: TEXT, fontWeight: 700 }}>{headline || "Untitled ad"}</h2>
            <button type="button" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", color: TEXT2, fontSize: 22, cursor: "pointer" }}>×</button>
          </div>
          <Field label="Description">
            <div style={{ fontSize: 14, lineHeight: 1.55, color: TEXT, whiteSpace: "pre-wrap" }}>{description || "—"}</div>
          </Field>
          <Field label="Call to action">
            <div style={{ fontSize: 14, fontWeight: 700, color: BLUE_DEEP }}>{cta}</div>
          </Field>
          {targetingList.length ? (
            <Field label="Audience">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {targetingList.map((t, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, color: TEXT2, background: BLUE_LIGHT, padding: "4px 10px", borderRadius: 999, border: `1px solid ${BORDER}` }}>{String(t)}</span>
                ))}
              </div>
            </Field>
          ) : null}
          {estimatedReach ? (
            <Field label="Estimated reach">
              <div style={{ fontSize: 24, fontWeight: 800, color: TEXT, letterSpacing: -0.5 }}>~{estimatedReach}</div>
              <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>People who could see this ad with a typical budget</div>
            </Field>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: "auto", paddingTop: 16 }}>
            {onEdit ? <Btn onClick={onEdit}>Edit</Btn> : null}
            {onRegenerate ? <Btn onClick={onRegenerate}>Regenerate</Btn> : null}
            {onLaunch ? <Btn primary onClick={onLaunch}>Launch campaign</Btn> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function Btn({ children, primary, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 700,
        color: primary ? WHITE : TEXT,
        background: primary ? `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)` : WHITE,
        border: primary ? "none" : `1px solid ${BORDER}`,
        cursor: "pointer",
        boxShadow: primary ? "0 8px 16px -8px rgba(14,165,233,0.55)" : "none",
      }}
    >
      {children}
    </button>
  );
}

// ─── Ad mockups ────────────────────────────────────────

function AdMockup({ adType, brand, headline, description, cta, asset, sitelinks, displaySize, compact }) {
  if (adType === "GoogleSearch")   return <GoogleSearchAd brand={brand} headline={headline} description={description} sitelinks={sitelinks} compact={compact} />;
  if (adType === "GoogleDisplay")  return <GoogleDisplayAd brand={brand} headline={headline} description={description} cta={cta} asset={asset} size={displaySize} compact={compact} />;
  if (adType === "MetaFeed")       return <MetaFeedAd brand={brand} headline={headline} description={description} cta={cta} asset={asset} compact={compact} />;
  if (adType === "InstagramStory") return <InstagramStoryAd brand={brand} headline={headline} description={description} cta={cta} asset={asset} compact={compact} />;
  if (adType === "TikTokInFeed")   return <TikTokInFeedAd brand={brand} headline={headline} description={description} cta={cta} asset={asset} compact={compact} />;
  return <MetaFeedAd brand={brand} headline={headline} description={description} cta={cta} asset={asset} compact={compact} />;
}

function GoogleSearchAd({ brand, headline, description, sitelinks, compact }) {
  const display = brand.displayUrl || `www.${(brand.name || "yourbrand").toLowerCase().replace(/\s/g, "")}.com`;
  return (
    <div style={{ background: WHITE, fontFamily: 'Arial, sans-serif', padding: compact ? 14 : 18, color: "#202124" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#202124", marginBottom: 4 }}>
        <span style={{ fontWeight: 700, padding: "1px 6px", border: "1px solid #202124", borderRadius: 4, fontSize: 11 }}>Sponsored</span>
        <span style={{ color: "#5f6368" }}>· {display}</span>
      </div>
      <div style={{ fontSize: compact ? 16 : 20, color: "#1a0dab", fontWeight: 400, lineHeight: 1.3, marginBottom: 2, cursor: "pointer" }}>
        {headline || "Your headline here"}
      </div>
      <div style={{ fontSize: 13, color: "#4d5156", lineHeight: 1.45 }}>
        {(description || "Your ad description shows here. Keep it benefit-led and specific.").slice(0, compact ? 90 : 200)}
      </div>
      {sitelinks && sitelinks.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: compact ? 4 : 8, marginTop: 10 }}>
          {sitelinks.slice(0, 4).map((s, i) => (
            <div key={i} style={{ fontSize: 12.5, color: "#1a0dab" }}>{s.title}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function GoogleDisplayAd({ brand, headline, description, cta, asset, size, compact }) {
  const dims = (size || "300x250").split("x").map((n) => parseInt(n, 10));
  const w = dims[0] || 300;
  const h = dims[1] || 250;
  const horizontal = w > h * 1.7;
  const placeholderImg = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0EA5E9"/><stop offset="1" stop-color="#0284C7"/></linearGradient></defs><rect width="400" height="400" fill="url(#g)"/></svg>');
  return (
    <div style={{
      width: "100%",
      aspectRatio: `${w} / ${h}`,
      background: "#fff",
      border: "1px solid #DADCE0",
      display: "flex",
      flexDirection: horizontal ? "row" : "column",
      position: "relative",
      overflow: "hidden",
      fontFamily: "Roboto, Arial, sans-serif",
    }}>
      <div style={{ flex: horizontal ? "0 0 35%" : "1 1 60%", background: `center/cover no-repeat url(${asset?.url || placeholderImg})`, position: "relative" }}>
        <span style={{ position: "absolute", top: 6, left: 6, fontSize: 9, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,0.55)", padding: "2px 6px", borderRadius: 3, letterSpacing: 0.4 }}>AD</span>
      </div>
      <div style={{ flex: 1, padding: compact ? 8 : 12, display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
        <div>
          <div style={{ fontSize: 11, color: "#5f6368", fontWeight: 600, marginBottom: 4 }}>{brand.name || "Your brand"}</div>
          <div style={{ fontSize: compact ? 13 : 15, fontWeight: 700, color: "#202124", lineHeight: 1.25, marginBottom: 4 }}>{(headline || "Your bold headline").slice(0, 30)}</div>
          {!horizontal ? (
            <div style={{ fontSize: 11, color: "#5f6368", lineHeight: 1.4 }}>{(description || "Short benefit").slice(0, 60)}</div>
          ) : null}
        </div>
        <button style={{ alignSelf: "flex-start", padding: "5px 12px", borderRadius: 4, background: BLUE, color: "#fff", fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer", marginTop: 6 }}>{cta || "Learn More"}</button>
      </div>
    </div>
  );
}

function MetaFeedAd({ brand, headline, description, cta, asset, compact }) {
  const placeholderImg = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0EA5E9"/><stop offset="1" stop-color="#0284C7"/></linearGradient></defs><rect width="600" height="600" fill="url(#g)"/></svg>');
  return (
    <div style={{ background: WHITE, fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', fontSize: 14, color: "#050505" }}>
      <div style={{ display: "flex", alignItems: "center", padding: 12, gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: `center/cover url(${brand.avatarUrl || ""}), linear-gradient(135deg,#0EA5E9,#0284C7)`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
          {!brand.avatarUrl ? (brand.name || "Y").trim()[0] : null}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{brand.name || "Your Brand"}</div>
          <div style={{ fontSize: 11, color: "#65676b" }}>Sponsored · 🌍</div>
        </div>
        <span style={{ color: "#65676b", fontSize: 18 }}>···</span>
      </div>
      <div style={{ padding: "0 12px 10px", fontSize: 14, color: "#050505", lineHeight: 1.45 }}>
        {(description || "").slice(0, compact ? 80 : 220)}
      </div>
      <div style={{ width: "100%", aspectRatio: "1.91/1", background: `center/cover no-repeat url(${asset?.url || placeholderImg})` }} />
      <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f0f2f5", borderBottom: "1px solid #e4e6eb" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "#65676b", textTransform: "uppercase" }}>{brand.displayUrl || "yourwebsite.com"}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#050505" }}>{(headline || "Your headline").slice(0, 48)}</div>
        </div>
        <button style={{ padding: "8px 14px", background: "#e4e6eb", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, color: "#050505", cursor: "pointer", whiteSpace: "nowrap", marginLeft: 10 }}>{cta || "Learn More"}</button>
      </div>
      <div style={{ display: "flex", borderTop: "1px solid #e4e6eb" }}>
        {["Like", "Comment", "Share"].map((l) => (
          <button key={l} style={{ flex: 1, padding: "9px 0", background: "transparent", border: "none", color: "#65676b", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>{l}</button>
        ))}
      </div>
    </div>
  );
}

function InstagramStoryAd({ brand, headline, description, cta, asset, compact }) {
  const placeholderImg = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="1067" viewBox="0 0 600 1067"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f09433"/><stop offset=".5" stop-color="#dc2743"/><stop offset="1" stop-color="#bc1888"/></linearGradient></defs><rect width="600" height="1067" fill="url(#g)"/></svg>');
  return (
    <div style={{ position: "relative", aspectRatio: "9/16", background: `center/cover url(${asset?.url || placeholderImg})`, overflow: "hidden", color: "#fff", fontFamily: '-apple-system, system-ui, sans-serif' }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 100%)" }} />
      <div style={{ position: "absolute", top: 10, left: 10, right: 10, display: "flex", gap: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, height: 2, background: "rgba(255,255,255,0.35)", borderRadius: 2 }}>
            <div style={{ width: i === 0 ? "30%" : "0%", height: "100%", background: "#fff", borderRadius: 2 }} />
          </div>
        ))}
      </div>
      <div style={{ position: "absolute", top: 20, left: 10, right: 10, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{(brand.name || "Y").trim()[0]}</div>
        <div style={{ fontWeight: 700 }}>{brand.name || "Your Brand"}</div>
        <div style={{ fontSize: 11, opacity: 0.8 }}>· Sponsored</div>
      </div>
      <div style={{ position: "absolute", left: 18, right: 18, bottom: 96, textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>
        <div style={{ fontSize: compact ? 18 : 22, fontWeight: 800, lineHeight: 1.2, marginBottom: 6 }}>{(headline || "Your hook here").slice(0, compact ? 36 : 72)}</div>
        <div style={{ fontSize: 13, opacity: 0.95, lineHeight: 1.4 }}>{(description || "").slice(0, compact ? 64 : 140)}</div>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 20, display: "flex", justifyContent: "center" }}>
        <button style={{ padding: "10px 22px", borderRadius: 999, background: "rgba(255,255,255,0.95)", color: "#0A1B3A", fontSize: 13, fontWeight: 800, border: "none", cursor: "pointer", boxShadow: "0 6px 16px rgba(0,0,0,0.25)" }}>{cta || "Learn More"} ›</button>
      </div>
    </div>
  );
}

function TikTokInFeedAd({ brand, headline, description, cta, asset, compact }) {
  const placeholderImg = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="600" height="1067" viewBox="0 0 600 1067"><rect width="600" height="1067" fill="#111"/></svg>');
  const isVideo = asset?.type === "video";
  return (
    <div style={{ position: "relative", aspectRatio: "9/16", background: "#000", overflow: "hidden", color: "#fff", fontFamily: '-apple-system, system-ui, sans-serif' }}>
      {isVideo && asset?.url ? (
        <video src={asset.url} muted loop autoPlay playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <div style={{ position: "absolute", inset: 0, background: `center/cover url(${asset?.url || placeholderImg})` }} />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.65) 100%)" }} />
      <div style={{ position: "absolute", top: 14, left: 0, right: 0, display: "flex", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>For You</div>
      <div style={{ position: "absolute", right: 10, bottom: 100, display: "flex", flexDirection: "column", gap: 18, alignItems: "center" }}>
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.16)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>{(brand.name || "Y").trim()[0]}</div>
        {sideAction("♥", "12k")}
        {sideAction("💬", "421")}
        {sideAction("↗", "Share")}
      </div>
      <div style={{ position: "absolute", left: 12, right: 70, bottom: 70, textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
        <div style={{ display: "inline-block", fontSize: 10, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.15)", padding: "3px 8px", borderRadius: 4, marginBottom: 6, letterSpacing: 0.6 }}>SPONSORED</div>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>@{(brand.handle || (brand.name || "yourbrand").toLowerCase().replace(/\s/g, ""))}</div>
        <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>{(description || headline || "").slice(0, compact ? 80 : 180)}</div>
      </div>
      <div style={{ position: "absolute", left: 12, right: 12, bottom: 18 }}>
        <button style={{ width: "100%", padding: "10px 16px", borderRadius: 6, background: "#FE2C55", color: "#fff", fontWeight: 800, fontSize: 14, border: "none", cursor: "pointer" }}>{cta || "Learn More"} ›</button>
      </div>
    </div>
  );
}

function sideAction(glyph, label) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, fontSize: 11, fontWeight: 700, color: "#fff" }}>
      <div style={{ fontSize: 22, lineHeight: 1 }}>{glyph}</div>
      <span>{label}</span>
    </div>
  );
}
